import { ApiHelper } from '../../ApiHelper';
import type {
    AgentTaskProposedChangeCollectionResult,
    AgentTaskProposedChangeFilters,
    AgentTaskProposedChangeResultSet,
    AgentTaskProposedChangesSortKey,
    AgentTaskProposedChangesView,
    AgentTaskProposalVote,
    AgentTaskProposalVoteUpdateResult,
    AgentTaskSortDirection,
} from './types';
import { normalizeAgentTaskQuestion } from './questionsApi';
import { normalizeAgentTaskProposedChange } from './workItemApi';

type ApiRecord = Record<string, unknown>;

export interface AgentTaskProposedChangesQuery extends AgentTaskProposedChangeFilters {
    view: AgentTaskProposedChangesView;
    pageNumber: number;
    pageSize: number;
    sortBy: AgentTaskProposedChangesSortKey;
    sortDirection: AgentTaskSortDirection;
}

function asRecord(value: unknown): ApiRecord {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as ApiRecord : {};
}

function first(record: ApiRecord, ...keys: string[]) {
    for (const key of keys) {
        if (record[key] !== undefined && record[key] !== null) return record[key];
    }
    return undefined;
}

function number(value: unknown, fallback = 0) {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function optionalNumber(value: unknown) {
    const parsed = number(value, NaN);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function optionalString(value: unknown) {
    return typeof value === 'string' && value.trim() ? value : undefined;
}

export function normalizeAgentTaskProposedChangeResultSet(value: unknown): AgentTaskProposedChangeResultSet {
    const record = asRecord(value);
    const rawEvents = first(record, 'proposedChangeEvents', 'ProposedChangeEvents', 'changes', 'Changes');
    const rawQuestions = first(record, 'questions', 'Questions', 'clarifications', 'Clarifications');
    const rawAssumptions = first(record, 'assumptions', 'Assumptions');
    const rawPullRequests = first(record, 'pullRequests', 'PullRequests');
    const newerAttempt = asRecord(first(record, 'newerAttempt', 'NewerAttempt'));

    return {
        taskListId: number(first(record, 'taskListId', 'TaskListId')),
        taskId: number(first(record, 'taskId', 'TaskId')),
        taskScope: optionalString(first(record, 'taskScope', 'TaskScope', 'scope', 'Scope')) ?? '',
        snapshotId: number(first(record, 'snapshotId', 'SnapshotId')),
        taskSnapshotInclusivePercent: optionalNumber(first(record, 'taskSnapshotInclusivePercent', 'TaskSnapshotInclusivePercent')),
        workItemId: number(first(record, 'workItemId', 'WorkItemId')),
        jobName: optionalString(first(record, 'jobName', 'JobName')) ?? '',
        attemptNumber: optionalNumber(first(record, 'attemptNumber', 'AttemptNumber')),
        jobStatus: number(first(record, 'jobStatus', 'JobStatus')),
        pullRequestStatus: number(first(record, 'pullRequestStatus', 'PullRequestStatus')),
        pullRequestHealth: optionalNumber(first(record, 'pullRequestHealth', 'PullRequestHealth')),
        prId: optionalNumber(first(record, 'prId', 'PrId', 'PRId')),
        agentStartDate: optionalString(first(record, 'agentStartDate', 'AgentStartDate')),
        agentEndDate: optionalString(first(record, 'agentEndDate', 'AgentEndDate')),
        resultSummary: optionalString(first(record, 'resultSummary', 'ResultSummary')),
        assumptions: (Array.isArray(rawAssumptions) ? rawAssumptions : [])
            .filter((item): item is string => typeof item === 'string' && !!item.trim()),
        noChangeReason: optionalString(first(record, 'noChangeReason', 'NoChangeReason')),
        expectedReductionPercent: optionalNumber(first(record, 'expectedReductionPercent', 'ExpectedReductionPercent')),
        expectedSnapshotReductionPercent: optionalNumber(first(record, 'expectedSnapshotReductionPercent', 'ExpectedSnapshotReductionPercent')),
        newerAttemptWorkItemId: optionalNumber(
            first(record, 'newerAttemptWorkItemId', 'NewerAttemptWorkItemId')
            ?? first(newerAttempt, 'workItemId', 'WorkItemId'),
        ),
        newerAttemptStatus: optionalNumber(
            first(record, 'newerAttemptStatus', 'NewerAttemptStatus')
            ?? first(newerAttempt, 'jobStatus', 'JobStatus', 'status', 'Status'),
        ),
        pullRequests: (Array.isArray(rawPullRequests) ? rawPullRequests : []).flatMap(value => {
            const pullRequest = asRecord(value);
            const prId = optionalNumber(first(pullRequest, 'prId', 'PrId', 'PRId'));
            return prId === undefined ? [] : [{
                prId,
                pullRequestStatus: number(first(pullRequest, 'pullRequestStatus', 'PullRequestStatus')),
                pullRequestHealth: optionalNumber(first(pullRequest, 'pullRequestHealth', 'PullRequestHealth')),
            }];
        }),
        proposedChangeEvents: (Array.isArray(rawEvents) ? rawEvents : []).map(normalizeAgentTaskProposedChange),
        questions: (Array.isArray(rawQuestions) ? rawQuestions : []).map(normalizeAgentTaskQuestion),
    };
}

export function normalizeAgentTaskProposedChangeCollection(
    value: unknown,
    query: Pick<AgentTaskProposedChangesQuery, 'pageNumber' | 'pageSize'>,
): AgentTaskProposedChangeCollectionResult {
    const record = asRecord(value);
    const rawResultSets = first(record, 'resultSets', 'ResultSets', 'items', 'Items', 'value', 'Value');
    const resultSets = (Array.isArray(rawResultSets) ? rawResultSets : Array.isArray(value) ? value : [])
        .map(normalizeAgentTaskProposedChangeResultSet)
        .filter(item => item.workItemId > 0);

    return {
        resultSets,
        totalCount: number(first(record, 'totalCount', 'TotalCount', 'count', 'Count'), resultSets.length),
        pageNumber: number(first(record, 'pageNumber', 'PageNumber', 'page', 'Page'), query.pageNumber),
        pageSize: number(first(record, 'pageSize', 'PageSize'), query.pageSize),
    };
}

export function normalizeAgentTaskProposalVoteUpdateResult(value: unknown): AgentTaskProposalVoteUpdateResult {
    const record = asRecord(value);
    const rawProposals = first(record, 'proposals', 'Proposals');
    return {
        proposals: (Array.isArray(rawProposals) ? rawProposals : []).map(normalizeAgentTaskProposedChange),
        activeProposalCount: number(first(record, 'activeProposalCount', 'ActiveProposalCount')),
        votedProposalCount: number(first(record, 'votedProposalCount', 'VotedProposalCount')),
        okProposalCount: number(first(record, 'okProposalCount', 'OkProposalCount')),
    };
}

export function buildAgentTaskProposedChangesUrl(serviceName: string, query: AgentTaskProposedChangesQuery) {
    const search = new URLSearchParams({
        view: query.view,
        pageNumber: String(query.pageNumber),
        pageSize: String(query.pageSize),
        sortBy: query.sortBy,
        sortDirection: query.sortDirection,
    });
    if (query.taskListId !== undefined) search.set('taskListId', String(query.taskListId));
    if (query.taskId !== undefined) search.set('taskId', String(query.taskId));
    if (query.workItemId !== undefined) search.set('workItemId', String(query.workItemId));
    if (query.candidateId?.trim()) search.set('candidateId', query.candidateId.trim());
    if (query.jobName?.trim()) search.set('jobName', query.jobName.trim());
    return `/api/service/${encodeURIComponent(serviceName)}/agenttasks/changes?${search.toString()}`;
}

export function buildAgentTaskProposedChangesPageUrl(
    serviceName: string,
    filters: AgentTaskProposedChangeFilters = {},
) {
    const search = new URLSearchParams();
    if (filters.taskListId !== undefined) search.set('taskListId', String(filters.taskListId));
    if (filters.taskId !== undefined) search.set('taskId', String(filters.taskId));
    if (filters.workItemId !== undefined) search.set('workItemId', String(filters.workItemId));
    if (filters.candidateId?.trim()) search.set('candidateId', filters.candidateId.trim());
    if (filters.jobName?.trim()) search.set('jobName', filters.jobName.trim());
    const suffix = search.toString();
    return `/service/${encodeURIComponent(serviceName)}/agenttasks/changes${suffix ? `?${suffix}` : ''}`;
}

export async function getAgentTaskProposedChanges(
    serviceName: string,
    query: AgentTaskProposedChangesQuery,
) {
    const response = await ApiHelper(buildAgentTaskProposedChangesUrl(serviceName, query));
    return normalizeAgentTaskProposedChangeCollection(response, query);
}

export async function updateAgentTaskProposedChangeVote(
    serviceName: string,
    analysisItemId: number,
    vote: AgentTaskProposalVote | null,
    rowVersion: string,
) {
    const response = await ApiHelper(
        `/api/service/${encodeURIComponent(serviceName)}/agenttasks/changes/${analysisItemId}`,
        'PATCH',
        { vote, rowVersion },
    );
    return normalizeAgentTaskProposalVoteUpdateResult(response);
}

export async function okAllAgentTaskProposedChanges(
    serviceName: string,
    taskListId: number,
    taskId: number,
) {
    const response = await ApiHelper(
        `/api/service/${encodeURIComponent(serviceName)}/agenttasklists/${taskListId}/tasks/${taskId}/changes:okall`,
        'POST',
        null,
    );
    return normalizeAgentTaskProposalVoteUpdateResult(response);
}
